import { AttantionTriangleCustom, CloseRoundLight, DownloadAltLight, SortDownLight, SortUpLight } from '@repo/go-icons';
import { Button, LoaderIcon, Tooltip } from '@repo/go-ui';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { useService } from 'src/packages/di';

import styles from './UserExportTasks.module.scss';

type Props = {
  onClose: () => void;
};

export const UserExportTasks = observer(function UserFiles({ onClose }: Props) {
  const userFilesService = useService('generatedUserFilesService');

  const { t } = useTranslation();

  if (userFilesService.isLoading) {
    return (
      <div className={styles.userFilesLoading}>
        <LoaderIcon size="s" />
      </div>
    );
  }

  return (
    <div>
      <div className={styles.userFilesHeader}>
        <div className={styles.userFilesTitle}>{t('dashboard:UserFiles.title')}</div>
        <button type="button" className={styles.userFilesSort} onClick={() => userFilesService.toggleSortOrder()}>
          <div>{t('dashboard:UserFiles.date')}</div>
          <div>
            {userFilesService.sortOrder === 'asc' ? (
              <SortDownLight className={styles.userFilesSortIcon} />
            ) : (
              <SortUpLight className={styles.userFilesSortIcon} />
            )}
          </div>
        </button>
        <Button onClick={onClose} variant="flat" icon={<CloseRoundLight className={styles.crossIcon} />} />
      </div>
      {userFilesService.sortedTasks.length === 0 && (
        <div className={styles.userFilesEmpty}>{t('dashboard:UserFiles.empty')}</div>
      )}
      <div className={styles.userFilesList}>
        {userFilesService.sortedTasks.map((file) => {
          const fileName =
            file.fileName ?? `${file.wellName} ${format(new Date(file.createdAt), 'ddMMyyyy HHmmss')}.csv`;
          return (
            <>
              {file.status === 'PROCESSING' && (
                <div className={styles.userFilesItem} key={file.taskId}>
                  <div className={styles.userFilesItemTitle}>{fileName}</div>
                  <div>
                    <LoaderIcon size="s" />
                  </div>
                </div>
              )}
              {file.status === 'COMPLETED' && file.downloadLink && (
                <a
                  className={styles.userFilesItem}
                  href={file.downloadLink}
                  target="_blank"
                  rel="noreferrer"
                  download={fileName}
                  title={file.fileName}
                  key={file.taskId}
                >
                  <div className={styles.userFilesItemTitle}>{fileName}</div>
                  <DownloadAltLight className={styles.downloadIcon} />
                </a>
              )}
              {file.status === 'FAILED' && (
                <div className={styles.userFilesItem} key={file.taskId}>
                  <div className={styles.userFilesItemTitle}>{fileName}</div>
                  <Tooltip title={file.errorMessage}>
                    <AttantionTriangleCustom className={styles.attentionIcon} />
                  </Tooltip>
                </div>
              )}
            </>
          );
        })}
      </div>
    </div>
  );
});
